import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, inject, ViewChild } from '@angular/core';
import { ConfirmModalComponent, ModalComponent } from '@catch/general-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { map, shareReplay, switchMap, takeWhile } from 'rxjs';
import { ButtonComponent } from '../button/button.component';
import { TosAdapter } from '../../adapters/tos.adapter';
import { AppState } from '../../app.reducer';
import { CompanyUserRole } from '../../enums/company-user.role.enum';
import { logout } from '../../store/auth/auth.actions';
import { hasPermission } from '../../store/auth/auth.reducer';
import { skipTos } from '../../store/config/config.actions';
import { selectTosSkip } from '../../store/config/config.reducer';
import { selectIsCatchUser } from '../../store/user-account/user-account.reducer';

@UntilDestroy()
@Component({
  selector: 'app-tos-modal',
  templateUrl: './tos-modal.component.html',
  styleUrls: ['./tos-modal.component.scss'],
  imports: [ModalComponent, ConfirmModalComponent, TranslocoPipe, DatePipe, ButtonComponent],
})
export class TosModalComponent implements AfterViewInit {
  private readonly store = inject(Store<AppState>);
  private readonly tosAdapter = inject(TosAdapter);

  @ViewChild('modal') modal: ModalComponent;
  latestChange!: DateTime;

  ngAfterViewInit(): void {
    let modalOpened = false;
    const tosAccepted$ = this.tosAdapter
      .getTosAccepted()
      .pipe(shareReplay(1))
      .pipe(
        untilDestroyed(this),
        map((result) => {
          this.latestChange = DateTime.fromISO(result.latestChange);
          return result.accepted;
        })
      );

    tosAccepted$
      .pipe(
        switchMap((accepted) => {
          return this.store.select(selectTosSkip).pipe(
            map((tosSkip) => {
              const nextShow = tosSkip.toUnixInteger();
              const now = DateTime.now().toUnixInteger();
              const differenceHours = nextShow - now;
              const showNow = differenceHours <= 0;
              return showNow && !accepted;
            })
          );
        }),
        switchMap((show) => {
          return this.store
            .select(hasPermission(CompanyUserRole.ADMIN))
            .pipe(map((hasPermission) => hasPermission && show));
        }),
        switchMap((show) => {
          return this.store
            .select(selectIsCatchUser)
            .pipe(map((catchUser) => !catchUser && show));
        }),
        takeWhile((show) => {
          if (modalOpened) {
            return false;
          } else {
            modalOpened = show;
            return true;
          }
        })
      )
      .subscribe((show) => {
        if (show) this.modal.open();
      });
  }

  decline() {
    this.tosAdapter.declineTos().subscribe(() => {
      this.store.dispatch(logout());
    });
  }

  skip() {
    this.store.dispatch(skipTos());
    this.modal.close();
  }

  accept() {
    this.tosAdapter.acceptsTos().subscribe(() => {
      window.location.reload();
    });
  }
}
